import { Award, Users, Clock, Heart, MapPin, Calendar, User, Briefcase, GraduationCap, Phone } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';

export default function AboutPage() {
  const { language, t } = useLanguage();

  const stats = [
    {
      icon: Users,
      value: '1000+',
      label: { en: 'Satisfied Patients', fr: 'Patients Satisfaits' }
    },
    {
      icon: Award,
      value: '8+',
      label: { en: 'Years of Experience', fr: 'Années d\'Expérience' }
    },
    {
      icon: Clock,
      value: '24/7',
      label: { en: 'Emergency Service', fr: 'Service d\'Urgence' }
    },
    {
      icon: Heart,
      value: '100%',
      label: { en: 'Patient Dedication', fr: 'Dévouement aux Patients' }
    },
  ];

  return (
    <div className="min-h-screen pt-28 pb-20 bg-gradient-to-br from-blue-50 via-white to-amber-50">
      <div className="container mx-auto px-4">
        {/* Hero Section */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-blue-900 mb-4">
            {t('About Clinique Mains Douces Plus', 'À Propos de Clinique Mains Douces Plus')}
          </h1>
          <p className="text-base sm:text-lg text-gray-600">
            {t(
              'Your trusted healthcare partner in Tradex Simbock, providing quality medical care since 2018',
              'Votre partenaire de confiance en matière de soins de santé à Tradex Simbock, fournissant des soins médicaux de qualité depuis 2018'
            )}
          </p>
        </div>

        {/* Mission Section */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="/equipe2.jpeg"
                alt="Clinique Mains Douces Plus"
                className="w-full h-auto object-cover aspect-[4/3]"
              />
            </div>
            <div className="absolute -bottom-4 -right-4 bg-white p-4 rounded-xl shadow-xl">
              <p className="text-blue-900 font-bold flex items-center gap-2">
                <MapPin size={20} className="text-amber-600" />
                <span>Tradex Simbock, Yaoundé</span>
              </p>
            </div>
          </div>

          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 mb-6">
              {t('Our Story', 'Notre Histoire')}
            </h2>
            
            <div className="space-y-4 text-gray-700 mb-6">
              <p className="leading-relaxed">
                {t(
                  'Founded in 2018 as "Centre de Santé Mains Douces Plus" under the leadership of Mme. ZEFO KITIO REGINE, our healthcare facility has been serving the Yaoundé community for over 8 years. In 2025, we officially became Clinique Mains Douces Plus, marking our evolution into a fully equipped medical clinic.',
                  'Fondé en 2018 sous le nom de "Centre de Santé Mains Douces Plus" sous la direction de Mme. ZEFO KITIO REGINE, notre établissement de santé dessert la communauté de Yaoundé depuis plus de 8 ans. En 2025, nous sommes officiellement devenus Clinique Mains Douces Plus, marquant notre évolution vers une clinique médicale entièrement équipée.'
                )}
              </p>
              <p className="leading-relaxed">
                {t(
                  'Located in Tradex Simbock, we pride ourselves on offering a welcoming environment where every patient receives the personalized attention they deserve. Our team of qualified healthcare professionals combines medical expertise with compassion to ensure you receive the best possible care.',
                  'Située à Tradex Simbock, nous sommes fiers d\'offrir un environnement accueillant où chaque patient reçoit l\'attention personnalisée qu\'il mérite. Notre équipe de professionnels de santé qualifiés combine expertise médicale et compassion pour garantir que vous recevez les meilleurs soins possibles.'
                )}
              </p>
            </div>

            <div className="flex flex-wrap gap-4">
              <div className="bg-blue-100 px-6 py-3 rounded-lg">
                <p className="text-blue-900 font-semibold">
                  {t('Since 2018', 'Depuis 2018')}
                </p>
              </div>
              <div className="bg-amber-100 px-6 py-3 rounded-lg">
                <p className="text-amber-900 font-semibold">
                  {t('Qualified Staff', 'Personnel Qualifié')}
                </p>
              </div>
              <div className="bg-blue-100 px-6 py-3 rounded-lg">
                <p className="text-blue-900 font-semibold">
                  {t('24/7 Emergency', 'Urgence 24/7')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Director Section with user1.jpeg image */}
        <div className="bg-white rounded-3xl shadow-xl p-8 mb-16">
          <div className="flex flex-col md:flex-row items-center gap-8">
            {/* Director Image - Using user1.jpeg */}
            <div className="w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden border-4 border-amber-200 shadow-xl">
              <img 
                src="/user1.jpeg" 
                alt="Mme. ZEFO KITIO REGINE - Clinic Director"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="text-center md:text-left flex-1">
              <h2 className="text-2xl font-bold text-blue-900 mb-2">
                {t('Clinic Director', 'Directrice de la Clinique')}
              </h2>
              <p className="text-2xl md:text-3xl font-bold text-amber-600 mb-4">Mme. ZEFO KITIO REGINE</p>
              <p className="text-gray-700 leading-relaxed">
                {t(
                  'Under the leadership of Mme. ZEFO KITIO REGINE, Clinique Mains Douces Plus has grown from a health center to a fully equipped medical clinic, maintaining the same commitment to gentle care and medical excellence that has defined us since 1997.',
                  'Sous la direction de Mme. ZEFO KITIO REGINE, la Clinique Mains Douces Plus est passée d\'un centre de santé à une clinique médicale entièrement équipée, maintenant le même engagement envers des soins doux et l\'excellence médicale qui nous définit depuis 1997.'
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg text-center hover:shadow-2xl transition transform hover:-translate-y-2"
              >
                <div className="bg-gradient-to-br from-blue-50 to-amber-50 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Icon className="text-blue-600" size={40} />
                </div>
                <p className="text-3xl sm:text-4xl font-bold text-blue-900 mb-2">{stat.value}</p>
                <p className="text-gray-600 font-medium">
                  {language === 'en' ? stat.label.en : stat.label.fr}
                </p>
              </div>
            );
          })}
        </div>

        {/* History Timeline */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-3xl p-8 text-white mb-16">
          <h2 className="text-2xl sm:text-3xl font-bold mb-8 text-center">
            {t('Our Journey', 'Notre Parcours')}
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white/10 rounded-2xl p-6 backdrop-blur-sm">
              <Calendar className="text-amber-300 mb-4" size={32} />
              <h3 className="text-xl font-bold mb-3">2018</h3>
              <p className="text-blue-100">
                {t(
                  'Founded as "Centre de Santé Mains Douces Plus" by Mme. ZEFO KITIO REGINE',
                  'Fondé sous le nom "Centre de Santé Mains Douces Plus" par Mme. ZEFO KITIO REGINE'
                )}
              </p>
            </div>
            <div className="bg-white/10 rounded-2xl p-6 backdrop-blur-sm">
              <Heart className="text-amber-300 mb-4" size={32} />
              <h3 className="text-xl font-bold mb-3">2025</h3>
              <p className="text-blue-100">
                {t(
                  'Evolved into "Clinique Mains Douces Plus", expanding our services and facilities',
                  'Devenu "Clinique Mains Douces Plus", élargissant nos services et installations'
                )}
              </p>
            </div>
          </div>
        </div>


        {/* Values Section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-3xl p-8 text-white mb-12">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-6">
                {t('Our Core Values', 'Nos Valeurs Fondamentales')}
              </h2>
              <p className="text-blue-100 leading-relaxed mb-6">
                {t(
                  'At Clinique Mains Douces Plus, we are guided by principles that ensure the best care for our patients.',
                  'À la Clinique Mains Douces Plus, nous sommes guidés par des principes qui garantissent les meilleurs soins à nos patients.'
                )}
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Heart size={24} className="text-amber-300 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-amber-300 mb-1">
                      {t('Compassion', 'Compassion')}
                    </h3>
                    <p className="text-blue-100 text-sm">
                      {t('Gentle hands and caring hearts', 'Des mains douces et des cœurs attentionnés')}
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Award size={24} className="text-amber-300 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-amber-300 mb-1">
                      {t('Excellence', 'Excellence')}
                    </h3>
                    <p className="text-blue-100 text-sm">
                      {t('Maintaining the highest medical standards since 1997', 'Maintenir les normes médicales les plus élevées depuis 1997')}
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Users size={24} className="text-amber-300 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-amber-300 mb-1">
                      {t('Patient-Centered', 'Centré sur le Patient')}
                    </h3>
                    <p className="text-blue-100 text-sm">
                      {t('Putting your needs and comfort first', 'Placer vos besoins et votre confort en premier')}
                    </p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-white/10 rounded-2xl p-6 backdrop-blur-sm">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <MapPin className="text-amber-300" />
                {t('Visit Us in Simbock', 'Visitez-Nous à Simbock')}
              </h3>
              <div className="space-y-4">
                <p className="flex items-start gap-2">
                  <MapPin size={18} className="text-amber-300 mt-1 flex-shrink-0" />
                  <span>Tradex Simbock, Yaoundé, Cameroun</span>
                </p>
                <p className="flex items-start gap-2">
                  <Phone size={18} className="text-amber-300 mt-1 flex-shrink-0" />
                  <a href="tel:653474614" className="hover:text-amber-300 transition">
                   +237 653 474 614
                  </a>
                </p>
                <p className="flex items-start gap-2">
                  <Clock size={18} className="text-amber-300 mt-1 flex-shrink-0" />
                  <span>
                    {t('24/7 Emergency Services Available', 'Services d\'Urgence Disponibles 24h/24')}
                  </span>
                </p>
              </div>
              <div className="mt-6 pt-6 border-t border-blue-500">
                <Link
                  to="/contact"
                  className="block w-full bg-white text-blue-900 text-center px-6 py-3 rounded-lg font-semibold hover:bg-amber-50 transition"
                >
                  {t('Contact Us', 'Contactez-Nous')}
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <Link
            to="/contact"
            className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl font-semibold text-lg transition transform hover:scale-105 shadow-lg"
          >
            {t('Book an Appointment', 'Prendre Rendez-vous')}
          </Link>
        </div>
      </div>
    </div>
  );
}